import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ButtonLevel, Link, LinkSize } from '@vizit/atoms';
import { FEATURE_FLAGS, RETAILER_CONFIG_MAP, detectRetailerFromInput, getEnabledRetailerKeys } from '@vizit/constants';
import type { RetailerKey } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { FormTextField } from '@vizit/forms';
import { FormDialog } from '@vizit/forms';
import { PlusIcon } from '@vizit/icons';
import { RETAILER_ICONS } from '@vizit/molecules';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

type ScorePDPFormSchema = {
  product: string;
};

type ScorePDPSubmitData = ScorePDPFormSchema & {
  retailerKey: RetailerKey | null;
};

enum ScorePDPErrorType {
  None = 'none',
  Duplicate = 'duplicate',
  UnsupportedCategory = 'unsupported-category',
  ClassificationFailed = 'classification-failed',
  Generic = 'generic',
}

const ERROR_MESSAGES: Record<ScorePDPErrorType, string> = {
  [ScorePDPErrorType.None]: '',
  [ScorePDPErrorType.Duplicate]: 'This product has already been imported.',
  [ScorePDPErrorType.UnsupportedCategory]:
    "This product category isn't supported yet. Contact support for help or to request support for this category.",
  [ScorePDPErrorType.ClassificationFailed]: 'We are having trouble scoring this product. Contact support for help.',
  [ScorePDPErrorType.Generic]: 'Error importing product. Please try again in a few minutes.',
};

interface DialogDisplay {
  iconName: string | null;
  fieldLabel: string;
  placeholder: string;
  subtitle: string;
}

function resolveDialogDisplay(inputValue: string, enabledRetailerKeys: RetailerKey[]): DialogDisplay {
  const activeKey: RetailerKey | null = detectRetailerFromInput(inputValue, enabledRetailerKeys);
  const config = activeKey ? RETAILER_CONFIG_MAP[activeKey] : null;

  const fieldLabel = config?.itemIdLabel ?? 'Product ID';
  const displayName = config?.displayName;
  const defaultSubtitle = `Enter an ${enabledRetailerKeys.map((k) => RETAILER_CONFIG_MAP[k].displayName).join(' or ')} product ID below.`;

  return {
    iconName: config?.baseIcon ?? null,
    fieldLabel,
    placeholder: config ? `Enter ${fieldLabel}` : 'Enter product ID',
    subtitle: displayName ? `Enter the ${fieldLabel} for a ${displayName} product page below.` : defaultSubtitle,
  };
}

interface ScorePDPDialogProps {
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Callback to notify when the dialog open state changes
   * @param open - Whether the dialog should be open
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  errorType?: ScorePDPErrorType;
  triggerButtonText?: string;
  useIcon?: boolean;
  isLoading?: boolean;
  /**
   * The organization's configured retailer region (e.g. amazon_uk).
   * When it is an Amazon marketplace, ASIN input resolves to it instead of Amazon US.
   *
   * @optional
   * @type {RetailerKey | null}
   */
  orgRetailerKey?: RetailerKey | null;
  onSubmit: (data: ScorePDPSubmitData) => Promise<void>;
  onOpenDuplicate?: () => void;
}

function ScorePDPDialog(props: ScorePDPDialogProps) {
  const {
    isOpen,
    onOpenChange,
    hideTrigger = false,
    errorType = ScorePDPErrorType.None,
    triggerButtonText = 'Score product page',
    useIcon = false,
    isLoading = false,
    orgRetailerKey = null,
    onSubmit,
    onOpenDuplicate,
  } = props;

  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);
  const enabledRetailerKeys = React.useMemo(
    () => getEnabledRetailerKeys({ walmart: isRetailerEnabled, amazon: orgRetailerKey ?? undefined }),
    [isRetailerEnabled, orgRetailerKey],
  );

  const formSchema = React.useMemo(() => {
    return yup.object().shape({
      product: yup
        .string()
        .required('Product ID is required')
        .test('valid-product-id', 'Invalid product ID', function (value) {
          if (!value) return true;
          const detected = detectRetailerFromInput(value, enabledRetailerKeys);
          const config = detected ? RETAILER_CONFIG_MAP[detected] : null;
          if (config?.itemIdPattern.test(value)) return true;
          if (config) {
            return this.createError({
              message: `Please enter a valid ${config.displayName} ${config.itemIdLabel}`,
            });
          }
          const enabledNames = enabledRetailerKeys.map((k) => RETAILER_CONFIG_MAP[k].displayName).join(' or ');
          return this.createError({
            message: `Please enter a valid product ID for ${enabledNames}`,
          });
        }),
    });
  }, [enabledRetailerKeys]);

  const formMethods = useForm<ScorePDPFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues: { product: '' },
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, reset, setError, clearErrors, watch } = formMethods;

  const inputValue = watch('product');

  const display = React.useMemo(
    () => resolveDialogDisplay(inputValue ?? '', enabledRetailerKeys),
    [inputValue, enabledRetailerKeys],
  );

  const InputIconComponent = display.iconName ? RETAILER_ICONS[display.iconName] : null;
  const retailerIcon = InputIconComponent ? <InputIconComponent size={20} /> : null;

  const handleOnResetForm = () => {
    clearErrors();
    reset({ product: '' });
  };

  const handleFormSubmit = async (data: ScorePDPFormSchema) => {
    const retailerKey = detectRetailerFromInput(data.product, enabledRetailerKeys);
    await onSubmit({ ...data, retailerKey });
  };

  React.useEffect(() => {
    const message = ERROR_MESSAGES[errorType];
    if (message) {
      setError('product', { message });
    }
  }, [errorType, setError]);

  const isDuplicate = errorType === ScorePDPErrorType.Duplicate;
  const showOpenButton = isDuplicate && onOpenDuplicate;

  return (
    <FormDialog
      formMethods={formMethods}
      formName="score-pdp"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit(handleFormSubmit)}
      onResetForm={handleOnResetForm}
      data-testid="product-import-dialog"
    >
      {!hideTrigger && (
        <FormDialog.Trigger>
          <Button level={ButtonLevel.Primary} aria-label={triggerButtonText} data-testid="product-import-trigger">
            {useIcon ? <PlusIcon size={20} /> : triggerButtonText}
          </Button>
        </FormDialog.Trigger>
      )}
      <FormDialog.Title>Import a product page</FormDialog.Title>
      <FormDialog.Subtitle>{display.subtitle}</FormDialog.Subtitle>
      <FormDialog.FormFields>
        <FormTextField
          control={control}
          name="product"
          label={display.fieldLabel}
          placeholder={display.placeholder}
          isRequired
          isDisabled={isLoading}
          startIcon={retailerIcon}
          inputProps={{ 'data-testid': 'product-import-asin-input' }}
        />
        {showOpenButton && (
          <Link size={LinkSize.Sm} onPress={onOpenDuplicate} aria-label="Open existing product">
            Open product
          </Link>
        )}
      </FormDialog.FormFields>
      <FormDialog.SubmitButton data-testid="product-import-submit">Score my page!</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { ScorePDPDialog, ScorePDPErrorType, ERROR_MESSAGES as SCORE_PDP_ERROR_MESSAGES };
export type { ScorePDPFormSchema, ScorePDPSubmitData };
